import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { MsalProvider, useMsal, useIsAuthenticated, AuthenticatedTemplate, UnauthenticatedTemplate } from '@azure/msal-react';
import { PublicClientApplication, InteractionStatus } from '@azure/msal-browser';
import { msalConfig, loginRequest } from './config/authConfig';
import { RoleProvider } from './contexts/RoleContext';
import { AuthProvider } from './contexts/AuthContext';
import HomePage from './components/home/HomePage';
import RoleSelectionPage from './components/role-selection/RoleSelectionPage';
import AssessmentDashboard from './components/dashboard/AssessmentDashboard';
import GPSTeamDashboard from './components/gps/GPSTeamDashboard';
import GPSVibeCheckSurvey from './components/gps/GPSVibeCheckSurvey';
import QuestionnaireForm from './components/questionnaire/QuestionnaireForm';
import AssessmentDetails from './components/assessment/AssessmentDetails';
import AnalyticsDashboard from './components/analytics/AnalyticsDashboard';
import LeadingDashboard from './components/leading/LeadingDashboard';
import CohortManager from './components/leading/CohortManager';
import SurveyList from './components/leading/SurveyList';
import SurveyListGrouped from './components/leading/SurveyListGrouped';
import SurveyBuilderForm from './components/leading/SurveyBuilderForm';
import LeadingAnalyticsDashboard from './components/leading/LeadingAnalyticsDashboard';
import MySurveys from './components/leading/MySurveys';
import SurveyForm from './components/leading/SurveyForm';
import SurveySuccess from './components/leading/SurveySuccess';
import ConversationsDashboard from './components/conversations/ConversationsDashboard';
import MCSurveyForm from './components/conversations/MCSurveyForm';
import ProtectedRoute from './components/auth/ProtectedRoute';
import RoleProtectedRoute from './components/auth/RoleProtectedRoute';
import './App-gps-theme.css';

// Initialize MSAL instance
const msalInstance = new PublicClientApplication(msalConfig);

// Component to handle automatic redirect authentication
const AuthenticationGate = ({ children }) => {
  const { instance, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  useEffect(() => {
    const handleAuthentication = async () => {
      // If not authenticated and no interaction in progress, redirect to login
      if (!isAuthenticated && inProgress === InteractionStatus.None) {
        const accounts = instance.getAllAccounts();
        
        if (accounts.length === 0) {
          // No accounts found, initiate redirect login
          console.log('No authenticated user found, redirecting to Microsoft login...');
          try {
            await instance.loginRedirect(loginRequest);
          } catch (error) {
            console.error('Login redirect failed:', error);
          }
        } else {
          // Account exists, try silent token acquisition
          try {
            await instance.acquireTokenSilent({
              ...loginRequest,
              account: accounts[0],
            });
            console.log('Silent authentication successful');
          } catch (error) {
            console.log('Silent authentication failed, redirecting to login:', error);
            await instance.loginRedirect(loginRequest);
          }
        }
      }
    };

    handleAuthentication();
  }, [instance, isAuthenticated, inProgress]);

  // Show loading state while authentication is in progress
  if (inProgress !== InteractionStatus.None) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
      }}>
        <div style={{ textAlign: 'center', color: 'white' }}>
          <div style={{
            width: '64px',
            height: '64px',
            border: '6px solid rgba(255, 255, 255, 0.3)',
            borderTopColor: 'white',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
            margin: '0 auto 1.5rem auto'
          }}></div>
          <h2 style={{ fontSize: '1.5rem', fontWeight: '600', margin: '0 0 0.5rem 0' }}>
            Authenticating...
          </h2>
          <p style={{ fontSize: '1rem', opacity: 0.9, margin: 0 }}>
            Redirecting to Microsoft login
          </p>
        </div>
      </div>
    );
  }

  // Show loading state while checking authentication
  if (!isAuthenticated) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
      }}>
        <div style={{ textAlign: 'center', color: 'white' }}>
          <div style={{
            width: '64px',
            height: '64px',
            border: '6px solid rgba(255, 255, 255, 0.3)',
            borderTopColor: 'white',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
            margin: '0 auto 1.5rem auto'
          }}></div>
          <h2 style={{ fontSize: '1.5rem', fontWeight: '600', margin: '0 0 0.5rem 0' }}>
            Checking authentication...
          </h2>
        </div>
      </div>
    );
  }

  // User is authenticated, show the app
  return children;
};

function App() {
  return (
    <MsalProvider instance={msalInstance}>
      <AuthProvider>
        <RoleProvider>
          <Router>
            <AuthenticationGate>
              <div className="App">
                <Routes>
                  {/* Default landing - Participant Survey Dashboard */}
                  <Route path="/" element={<MySurveys />} />
                  
                  {/* App Selection Homepage - For admin mode */}
                  <Route path="/apps" element={<HomePage />} />
                  
                  {/* GPS Operational Excellence Routes - Admin */}
                  <Route path="/gps" element={<RoleProtectedRoute requiredRole="leading"><RoleSelectionPage /></RoleProtectedRoute>} />
                  <Route path="/gps/team/dashboard" element={<RoleProtectedRoute requiredRole="leading"><AssessmentDashboard /></RoleProtectedRoute>} />
                  <Route path="/gps/team/assessment/:assessmentId" element={<RoleProtectedRoute requiredRole="leading"><AssessmentDetails /></RoleProtectedRoute>} />
                  <Route path="/gps/team/analytics" element={<RoleProtectedRoute requiredRole="leading"><AnalyticsDashboard /></RoleProtectedRoute>} />
                  <Route path="/gps/leaders/dashboard" element={<RoleProtectedRoute requiredRole="leading"><AssessmentDashboard /></RoleProtectedRoute>} />
                  <Route path="/gps/leaders/assessment/:assessmentId" element={<RoleProtectedRoute requiredRole="leading"><AssessmentDetails /></RoleProtectedRoute>} />
                  <Route path="/gps/leaders/analytics" element={<RoleProtectedRoute requiredRole="leading"><AnalyticsDashboard /></RoleProtectedRoute>} />
                  {/* GPS Participant Route - no admin check */}
                  <Route path="/gps/pillar/:assignmentId/:pillarNumber" element={<GPSVibeCheckSurvey />} />
                  
                  {/* Leading at Gallo Routes */}
                  <Route path="/leading" element={<Navigate to="/leading/admin/cohorts" replace />} />
                  <Route 
                    path="/leading/dashboard" 
                    element={
                      <RoleProtectedRoute requiredRole="leading">
                        <LeadingDashboard />
                      </RoleProtectedRoute>
                    } 
                  />
                  
                  {/* Admin Routes */}
                  <Route 
                    path="/leading/admin/cohorts" 
                    element={
                      <RoleProtectedRoute requiredRole="leading">
                        <CohortManager />
                      </RoleProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/leading/admin/surveys" 
                    element={
                      <RoleProtectedRoute requiredRole="leading">
                        <SurveyList />
                      </RoleProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/leading/admin/surveys/grouped" 
                    element={
                      <RoleProtectedRoute requiredRole="leading">
                        <SurveyListGrouped />
                      </RoleProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/leading/admin/surveys/new" 
                    element={
                      <RoleProtectedRoute requiredRole="leading">
                        <SurveyBuilderForm />
                      </RoleProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/leading/admin/surveys/edit/:surveyId" 
                    element={
                      <RoleProtectedRoute requiredRole="leading">
                        <SurveyBuilderForm />
                      </RoleProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/leading/admin/analytics" 
                    element={
                      <RoleProtectedRoute requiredRole="leading">
                        <LeadingAnalyticsDashboard />
                      </RoleProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/leading/admin/participants" 
                    element={
                      <RoleProtectedRoute requiredRole="leading">
                        <div>Participant Management (Coming Soon)</div>
                      </RoleProtectedRoute>
                    } 
                  />
                  
                  {/* Participant Routes */}
                  <Route 
                    path="/leading/my-surveys" 
                    element={
                      <ProtectedRoute>
                        <MySurveys />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/leading/survey/:token" 
                    element={
                      <ProtectedRoute>
                        <SurveyForm />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/leading/survey/:token/success" 
                    element={
                      <ProtectedRoute>
                        <SurveySuccess />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/leading/notifications" 
                    element={
                      <ProtectedRoute>
                        <div>All Notifications (Coming Soon)</div>
                      </ProtectedRoute>
                    } 
                  />
                  
                  {/* Questionnaire Interface (role-agnostic) */}
                  <Route path="/q/:questionnaireId" element={<QuestionnaireForm />} />
                  
                  {/* Meaningful Conversations Routes */}
                  <Route 
                    path="/conversations" 
                    element={
                      <RoleProtectedRoute requiredRole="mc">
                        <ConversationsDashboard />
                      </RoleProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/conversations/survey/:token" 
                    element={
                      <ProtectedRoute>
                        <MCSurveyForm />
                      </ProtectedRoute>
                    } 
                  />
                  
                  {/* Legacy redirects for backward compatibility */}
                  <Route path="/team/dashboard" element={<Navigate to="/gps/team/dashboard" replace />} />
                  <Route path="/team/assessment/:assessmentId" element={<Navigate to="/gps/team/assessment/:assessmentId" replace />} />
                  <Route path="/team/analytics" element={<Navigate to="/gps/team/analytics" replace />} />
                  <Route path="/leaders/dashboard" element={<Navigate to="/gps/leaders/dashboard" replace />} />
                  <Route path="/leaders/assessment/:assessmentId" element={<Navigate to="/gps/leaders/assessment/:assessmentId" replace />} />
                  <Route path="/leaders/analytics" element={<Navigate to="/gps/leaders/analytics" replace />} />
                  <Route path="/dashboard" element={<Navigate to="/gps/team/dashboard" replace />} />
                  <Route path="/assessment/:assessmentId" element={<Navigate to="/gps/team/assessment/:assessmentId" replace />} />
                  
                  {/* 404 Page */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </div>
            </AuthenticationGate>
          </Router>
        </RoleProvider>
      </AuthProvider>
    </MsalProvider>
  );
}

// 404 Not Found component
const NotFound = () => {
  return (
    <div className="assessment-details-container">
      <div className="assessment-content">
        <div className="stats-card text-center">
          <h1 style={{ fontSize: '6rem', fontWeight: '800', marginBottom: '1rem', opacity: '0.3' }}>404</h1>
          <h2 style={{ fontSize: '2rem', fontWeight: '700', marginBottom: '1rem', color: '#111827' }}>Page Not Found</h2>
          <p style={{ color: '#6b7280', marginBottom: '2rem' }}>The page you're looking for doesn't exist.</p>
          <a 
            href="/"
            className="questionnaire-button questionnaire-button-primary"
          >
            Go to Homepage
          </a>
        </div>
      </div>
    </div>
  );
};

export default App;
